import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
// eslint-disable-next-line max-len
import { type ModelConfiguratorWithPermissionsSpecsFragment } from '../../../api/fragments/ModelConfiguratorWithPermissionsSpecs';
import { useGetModelConfiguratorQuery } from '../../../api/queries/getModelConfigurator';
import LoadingElement from '../../../components/LoadingElement';
import NotFoundResult from '../../../components/results/NotFoundResult';

type ModelConfiguratorContextValues = {
    modelConfigurator: ModelConfiguratorWithPermissionsSpecsFragment;
};

const ModelConfiguratorContext = createContext<ModelConfiguratorContextValues | null>(null);

export type ModelConfiguratorProviderProps = {
    isEdit?: boolean;
    modelConfiguratorId?: string;
    children: ReactNode | JSX.Element;
};

const ModelConfiguratorProvider = ({
    isEdit = false,
    modelConfiguratorId,
    children,
}: ModelConfiguratorProviderProps): JSX.Element => {
    const { data, loading } = useGetModelConfiguratorQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            id: modelConfiguratorId,
        },
        skip: !isEdit,
    });

    const modelConfigurator = useMemo<ModelConfiguratorWithPermissionsSpecsFragment>(
        () => data?.modelConfigurator,
        [data?.modelConfigurator]
    );

    const contextValues = useMemo<ModelConfiguratorContextValues>(
        () => ({
            modelConfigurator,
        }),
        [modelConfigurator]
    );

    if (isEdit && !modelConfigurator && loading) {
        return <LoadingElement />;
    }

    // TODO: permissions check here
    if (isEdit && !modelConfigurator) {
        return <NotFoundResult />;
    }

    return <ModelConfiguratorContext.Provider value={contextValues}>{children}</ModelConfiguratorContext.Provider>;
};

export default ModelConfiguratorProvider;

export const useModelConfigurator = () => {
    const context = useContext(ModelConfiguratorContext);

    if (!context) {
        throw new Error('Model configuration context is missing');
    }

    return context;
};
